<template>
  <div class="collection__container wrapper" v-if="products_count > 0 || template === 'search'">

      <!-- Sidebar -->
      <aside
        v-if="template !== 'search'"
        class="collection-sidebar"
        :class="{ 'with-image': image }">

        <div>
          <div class="breadcrumbs-back">
            <a href="/">
              <IconChevron stroke="#EEEEEE" :direction="'left'"></IconChevron>
              Home
            </a>
          </div>

          <img v-if="image" :src="image" :alt="title" />

          <div class="collection__sidebar-wrapper">
            <div v-if="description" class="collection__description">
              <p v-html="description"></p>
            </div>

            <div v-if="filtersApplied.length" @click="clearFilters()" class="collection__clear-filters">
              <span><IconClose stroke="#62AF31" />Remove Filters</span>
            </div>

            <!-- Filter By -->
            <div
              v-if="filterOptions.length"
              class="filter__group group--filter-by">
              <div
                :class="['filter__toggle', { 'open': filterByOpen }]"
                @click="filterByOpen = !filterByOpen; sortByOpen = false">
                <span class="filter__title">Filter By</span>
                <IconChevron stroke="#EEEEEE" :direction="filterByOpen ? 'up' : 'down'"></IconChevron>
              </div>
              <TransitionExpand>
                <div class="filter__dropdown" v-show="filterByOpen">
                  <div v-for="(filterGroup, index) in filterOptions" :key="index" class="filter__group--child">
                    <div
                      :class="['filter__toggle', { 'open': filtersOpen.includes(index) }]"
                      @click="toggleFilterBtn(index); sortByOpen = false">
                      <span class="filter__title">{{ filterGroup.label }}</span>
                      <IconChevron stroke="#EEEEEE" :direction="filtersOpen.includes(index) ? 'up' : 'down'"></IconChevron>
                    </div>
                    <TransitionExpand linear_margin=".5rem">
                      <div class="filter__dropdown--child" v-show="filtersOpen.includes(index)">
                        <span v-for="(option, index) in filterGroup.values"
                              :key="index"
                              @click="toggleFilter(option.key, stripKey(option.key), collectionID, filterGroup.filterOptionId, $event)"
                              :class="['filter__option', showActive(option.key)]">
                          <span>{{ stripKey(option.key) }}</span>
                        </span>
                      </div>
                    </TransitionExpand>
                  </div>
                </div>
              </TransitionExpand>
            </div>

            <!-- Sort By -->
            <div
              v-if="!noProductsfound"
              class="filter__group group--sort-by">
              <div :class="['filter__toggle', { 'open': sortByOpen }]" @click="sortByOpen = !sortByOpen; filterByOpen = false">
                <span class="filter__title">Sort By</span>
                <IconChevron stroke="#EEEEEE" :direction="sortByOpen ? 'up' : 'down'"></IconChevron>
              </div>
              <TransitionExpand>
                <div class="filter__dropdown" v-show="sortByOpen">
                  <span
                    @click="toggleFilter('sort by: new', 'new', collectionID)"
                    :class="[ 'filter__option', showActive('new')]">
                    New
                  </span>
                  <span
                    @click="toggleFilter('sort by: lowest price', 'lowest price', collectionID)"
                    :class="[ 'filter__option', showActive('lowest price')]">
                    Lowest Price
                  </span>
                  <span
                    @click="toggleFilter('sort by: highest price', 'highest price', collectionID)"
                    :class="[ 'filter__option', showActive('highest price')]">
                    Highest Price
                  </span>
                  <span
                    @click="toggleFilter('sort by: best sellers', 'best sellers', collectionID)"
                    :class="[ 'filter__option', showActive('best sellers')]">
                    Best Sellers
                  </span>
                </div>
              </TransitionExpand>
            </div>
          </div>
        </div>
      </aside>

      <div class="collection-main" :class="{ 'with-image': image }">

        <header role="banner" class="collection-title">
          <nav role="navigation" aria-label="breadcrumbs">
            <a href="/">{{ $language.breadcrumbs.home }}</a>
            <span aria-hidden="true">&gt;</span>
            <a :href="`/collections/${ handle }`">
              <span>{{ title }}</span>
            </a>
          </nav>
          <h1 v-if="template == 'search'" class="h2">Search results for <strong>{{ query }}</strong></h1>
          <h1 v-else class="h2">Shop our range of <strong>{{ title }}</strong></h1>
        </header>

        <!-- Products Grid -->
        <section class="product-grid" v-if="!noProductsfound">
          <ProductItem
            v-for="product in products"
            :product="product"
            :collection="handle"
            :key="product.id">
          </ProductItem>
        </section>

        <!-- Infinite scrolling pagination -->
        <infinite-loading
          class="collection__products-infinite-loading"
          v-if="!noProductsfound"
          :identifier="infiniteId"
          @infinite="infiniteHandler">
          <div slot="no-more"></div>
          <div slot="no-results"></div>
          <div slot="spinner">
            <div class="site-loading">
              <div class="half-circle-spinner">
                <div class="circle circle-1"></div>
                <div class="circle circle-2"></div>
              </div>
            </div>
          </div>
        </infinite-loading>

      </div>

  </div>

  <!-- No products to load -->
  <div v-else class="no-results">
    <header role="banner" class="collection-title" style="text-align: center;">
      <nav role="navigation" aria-label="breadcrumbs">
        <a href="/">{{ $language.breadcrumbs.home }}</a>
        <span aria-hidden="true">&gt;</span>
        <a :href="`/collections/${ handle }`">
          <span>{{ title }}</span>
        </a>
      </nav>
      <h1 class="h2">Shop our range of <strong>{{ title }}</strong></h1>
      <h3>Sorry, no products found</h3>
    </header>
  </div>
</template>

<script>
import ProductItem from '../components/ProductItem'
import InfiniteLoading from 'vue-infinite-loading'
import IconChevron from '../icons/IconChevron'
import IconClose from '../icons/IconClose'
import TransitionExpand from '../transitions/TransitionExpand'

export default {
  props: [ 'id', 'handle', 'title', 'description', 'metafields', 'image', 'products_count', 'template', 'query', 'sort_order' ],

  components: { ProductItem, InfiniteLoading, IconChevron, IconClose, TransitionExpand },

  data () {
    return {
      products         : [],
      filterOptions    : [],            // The filters returned by boost commerce API
      filterByOpen     : false,         // The 'filter by' dropdown is open
      filtersOpen      : [],            // The filters which are open inside of the 'filter by' dropdown
      filterTagsApplied: [],            // The current filters entire tags applied (colour: blue, colour: red)
      filtersApplied   : [],            // The current filters tag values applied (blue, red)
      sortByOpen       : false,         // The 'sort by' dropdown is open
      sortByApplied    : '',            // The current sort by value applied
      page             : 1,
      infiniteId       : +new Date(),
      noProductsfound  : null,
      allProductsLoaded: false,
      totalProducts    : 0
    }
  },

  mounted () {
    this.loadCollection()
    if (window.matchMedia('(min-width: 768px)').matches) {
      this.filterByOpen = true
    }
  },

  methods: {
    async loadCollection () {
      await this.getFilteredCollection({ collectionID: this.id, page: 1 })

      // Filters open by default
      for (const key in this.filterOptions) {
        if (window.matchMedia('(min-width: 768px)').matches) {
          if (!this.filterOptions[key].isCollapsePC) {
            this.filtersOpen.push(Number(key))
          }
        } else {
          if (!this.filterOptions[key].isCollapseMobile) {
            this.filtersOpen.push(Number(key))
          }
        }
      }

      this.products.length
        ? (this.page = 1, this.noProductsfound = false)
        : this.noProductsfound = true
    },

    quickshop (handle) {
      window.quickshop(handle)
    },

    toggleFilterBtn (idx) {
      if (this.filtersOpen.includes(idx)) {
        let index = this.filtersOpen.indexOf(idx)
        this.filtersOpen.splice(index, 1)
      } else {
        this.filtersOpen.push(idx)
      }
    },

    toggleFilter (tag, value, collectionID, filterOptionId) {
      // Reset products
      this.products = []

      if (!this.filtersApplied.includes(tag)) {
        // Add filter
        if (tag.includes('sort by')) {
          this.sortByApplied = value
        } else {
          this.filterTagsApplied.push(`${filterOptionId}:${value}`)
          this.filtersApplied.push(tag)
        }
      } else {
        // Remove filter
        this.filterTagsApplied.splice(this.filterTagsApplied.indexOf(`${filterOptionId}:${value}`), 1)
        this.filtersApplied.splice(this.filtersApplied.indexOf(tag), 1)
      }

      this.getFilteredCollection({ collectionID: this.id, page: 1 })
      this.page = 1
    },

    clearFilters () {
      this.filterTagsApplied = []
      this.filtersApplied = []
      this.products = []
      this.getFilteredCollection({ collectionID: this.id, page: 1 })
      this.page = 1
    },
      
    stripKey (tag) {
      if (/:(.+)/.exec(tag) && /:(.+)/.exec(tag).length) {
        return /:(.+)/.exec(tag)[1].trim()
      } else {
        return tag
      }
    },

    showActive (option) {
      if (this.filtersApplied.includes(option)) return 'active-filter'
      if (this.sortByApplied.includes(option)) return 'active-filter'
    },

    async getFilteredCollection ({ collectionID, page }) {
      // BCSF Endpoint
      let url = new URL('https://services.mybcapps.com/bc-sf-filter/')

      // Request options
      let params = {
        shop             : 'tacproaustralia.myshopify.com',
        page             : page,
        limit            : 12,
        product_available: false,
        build_filter_tree: true,
        collection_scope : collectionID
      }

      // Collection vs Search
      if (this.query) {
        url.pathname = url.pathname + 'search'
        params.q = this.query
      } else {
        url.pathname = url.pathname + 'filter'
      }

      // Set sort order
      if (this.sortByApplied) {
        switch (this.sortByApplied) {
          case 'new':
            params.sort = 'published-descending'
            break
          case 'lowest price':
            params.sort = 'price-ascending'
            break
          case 'highest price':
            params.sort = 'price-descending'
            break
          case 'best sellers':
            params.sort = 'best-selling-descending'
        }
      } else {
        params.sort = this.sort_order
      }

      // Create query string from params object
      Object.keys(params).forEach(key => {
        url.searchParams.append(key, params[key])
      })

      // Insert filters
      if (this.filterTagsApplied.length) {
        // let tags = []
        this.filterTagsApplied.forEach(filter => {
          let filterKey = filter.split(':')[0]
          let filterVal = filter.split(':')[1]
          url.searchParams.append(`${filterKey}[]`, filterVal)
          // Exclusive filtering
          // url.searchParams.append(`${filterKey}_and_condition`, false)
        })
      }

      // Send request
      await fetch(url)
        .then(response => response.json())
        .then(data => {
          let products = data.products
          if (products.constructor === Object) {
            // 0 products to show
            this.products = []
          } else {
            // Show products
            this.products.push(...products)
            this.allProductsLoaded = products.length < 20
            this.totalProducts = data.total_product
            // Build filter tree
            this.filterOptions = data.filter.options.filter(group => group.values.length)
            // Activate paginator
            this.infiniteId += 1
          }
        })
        .catch(error => {
          console.log('error', error)
        })
    },

    infiniteHandler: function ($state) {
      setTimeout(async function () {
        this.page += 1
        await this.getFilteredCollection({ collectionID: this.id, page: this.page })
        if (this.allProductsLoaded) {
          $state.complete()
        } else {
          $state.loaded()
        }
      }.bind(this), 1000) // Let the initial loadCollection function run first
    }
  }
}
</script>
